<template>
  <div class="order-item">
    <status-ribbon :status="order.status" />
    <div class="order-item-title">{{ titleText }}</div>
    <div class="order-item-body">
      <!-- 虚线是「信息区 → 操作/提示行」的分隔线：
           仅当下方有操作行（待支付/退款中）时才显示，无操作行的卡片（已支付/已取消/已退款）不显示 -->
      <div class="info-grid" :class="{ 'has-foot': hasFoot }">
        <div v-for="(col, ci) in columns" :key="ci" class="info-col">
          <p v-for="(line, li) in col" :key="li" class="info-line">
            <span class="info-label">{{ line.label }}：</span
            ><span class="info-value" :class="{ 'is-bold': line.bold }">{{
              line.value
            }}</span>
          </p>
        </div>
      </div>
      <div v-if="hasFoot" class="order-item-foot">
        <span class="tip" :class="{ 'tip-orange': isTipOrange }">
          {{ tipText }}
        </span>
        <div v-if="isUnpaid" class="actions">
          <span
            v-if="showCancel"
            class="btn btn-plain"
            @click="$emit('cancel', order)"
          >
            {{ text.cancelOrder }}
          </span>
          <span class="btn btn-primary" @click="$emit('pay', order)">
            {{ text.goPayOrder }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import StatusRibbon from "./StatusRibbon.vue";
import {
  ORDER_STATUS,
  ORDER_STATUS_TIP,
  PRO_PLAN_TEXT,
  PAY_COUNTDOWN_DEFAULT_SECONDS,
  ORDER_EXPIRE_RECHECK_INTERVAL,
  ORDER_EXPIRE_RECHECK_MAX,
} from "@/consts/proPlan";
import {
  formatDateTimeDot,
  formatDateDot,
  toTimestamp,
  capPayCountdown,
} from "@/utils/proPlan";

/**
 * 订单列表卡片
 * order: { id, orderNo, goodsName, price, payType, status, createTime,
 *          effectTime, expireTime, refundTime, cancelTime, expireRemainMs }
 * 布局对齐设计稿：标题行 + 信息栅格（2 列，按状态追加第 3 列；虚线分隔）+ 底部提示/操作行
 */
export default {
  name: "OrderListItem",
  components: { StatusRibbon },
  props: {
    order: {
      type: Object,
      required: true,
    },
    // 是否展示「取消订单」（后端取消接口未提供时由父级传 false 隐藏）
    showCancel: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      text: PRO_PLAN_TEXT,
      // 待支付倒计时：优先用后端返回的剩余时长；缺失时按「下单时间 + 支付时长」兜底，
      // 避免出现 00′00″
      remainMs: this.calcRemainMs(),
      timer: null,
      // 倒计时基准（本地时钟下的绝对截止时间戳），见 tick
      deadlineTs: 0,
      // 归零核对未果时的复查定时器（后端关单有延迟，见 scheduleRecheck）
      recheckTimer: null,
      recheckCount: 0,
    };
  },
  watch: {
    // 父级刷新列表、或核对后端后换入带 expireRemainMs 的新对象 → 重新同步倒计时。
    // 组件按 item.id 复用，mounted 不会二次执行，若不在此同步，
    // 列表刷新后卡片会一直停在 00′00″ 且计时器已停（禅道 Bug#23436）。
    order() {
      this.syncCountdown();
    },
  },
  computed: {
    isUnpaid() {
      return this.order.status === ORDER_STATUS.UNPAID;
    },
    /**
     * 卡片标题：需求 §6「专业版+套餐名称（套餐时长）」，如「专业版多比年卡（12个月）」。
     * 接口只给原样的 packageName（如「多比年卡多比」，不含「专业版」前缀），
     * 故此前缀与时长都由前端拼（时长格式与确认页「时长：12个月」一致）。
     * 套餐时长缺失时省略括号，避免出现「（）」（禅道 Bug#23433）。
     */
    titleText() {
      const o = this.order;
      const name = o.goodsName || "";
      const duration = o.months ? `（${o.months}个月）` : "";
      return `${this.text.proPrefix}${name}${duration}`;
    },
    columns() {
      const o = this.order;
      const base = [
        [
          { label: "订单编号", value: o.orderNo },
          { label: "下单时间", value: formatDateTimeDot(o.createTime) },
        ],
        [
          { label: "订单金额", value: `￥${o.price}`, bold: true },
          { label: "支付方式", value: o.payType },
        ],
      ];
      const extra = this.extraColumn;
      return extra ? [...base, extra] : base;
    },
    /**
     * 第 3 列由「订单状态」决定（设计稿 1967-20 各状态卡片）：
     *   已支付 / 退款失败 → 生效日期 + 到期日期
     *   已取消 / 已关闭   → 取消时间
     *   已退款           → 退款时间
     *   待支付 / 退款中   → 无第 3 列（设计稿仅两列）
     * 不能按字段有无来推断：退款中/已退款订单同样带 effectiveTime/entitlementEnd，
     * 按字段判断会把「生效日期 / 到期日期」显示出来，与设计稿不符（禅道 Bug#23434）。
     */
    extraColumn() {
      const o = this.order;
      switch (o.status) {
        case ORDER_STATUS.PAID:
        case ORDER_STATUS.REFUND_FAILED:
          // 两者都无值时退回两列，避免出现空标签
          if (!o.effectTime && !o.expireTime) return null;
          return [
            { label: "生效日期", value: formatDateDot(o.effectTime) },
            { label: "到期日期", value: formatDateDot(o.expireTime) },
          ];
        case ORDER_STATUS.CANCELED:
        case ORDER_STATUS.CLOSED:
          return o.cancelTime
            ? [{ label: "取消时间", value: formatDateTimeDot(o.cancelTime) }]
            : null;
        case ORDER_STATUS.REFUNDED:
          return o.refundTime
            ? [{ label: "退款时间", value: formatDateTimeDot(o.refundTime) }]
            : null;
        default:
          // 待支付(10) / 退款中(50)：设计稿无第 3 列
          return null;
      }
    },
    tipText() {
      if (this.isUnpaid) {
        return `订单将在${this.countdownText}后自动取消，请尽快支付`;
      }
      return ORDER_STATUS_TIP[this.order.status] || "";
    },
    // 是否存在底部操作/提示行（待支付、退款中才有）
    hasFoot() {
      return !!this.tipText;
    },
    isTipOrange() {
      return !!this.tipText;
    },
    countdownText() {
      const total = Math.max(0, Math.floor(this.remainMs / 1000));
      const m = Math.floor(total / 60);
      const s = total % 60;
      const mm = m < 10 ? "0" + m : "" + m;
      const ss = s < 10 ? "0" + s : "" + s;
      return `${mm}′${ss}″`;
    },
  },
  methods: {
    // 计算待支付剩余时长（毫秒）
    // 优先用父级核对后端后回填的 expireRemainMs（真实截止时间）；
    // 列表接口未返回支付截止时间，故兜底按「下单时间 + PAY_COUNTDOWN_MINUTES」推算。
    // TODO(待确认)：若后端在 list 中补 expireTime，应改为直接使用该字段。
    // 两种来源都经 capPayCountdown 截断，保证与确认页倒计时一致（均与后端同为 30 分钟）
    calcRemainMs() {
      const o = this.order || {};
      if (o.status !== ORDER_STATUS.UNPAID) return 0;
      if (o.expireRemainMs > 0) return capPayCountdown(o.expireRemainMs);
      const deadline =
        (toTimestamp(o.createTime) || Date.now()) +
        PAY_COUNTDOWN_DEFAULT_SECONDS * 1000;
      return capPayCountdown(Math.max(0, deadline - Date.now()));
    },
    startTimer() {
      if (this.timer || !this.isUnpaid || this.remainMs <= 0) return;
      // 基准截止时间：倒计时据此重算而非逐秒累减（后台标签页会节流 setInterval，
      // 累减法会让倒计时严重滞后，见 tick）
      this.deadlineTs = Date.now() + this.remainMs;
      this.timer = setInterval(() => this.tick(), 1000);
    },
    /**
     * 倒计时推进：按「绝对截止时间 − 当前时间」重算。
     *
     * 浏览器会节流后台标签页的 setInterval（Chrome 隐藏约 5 分钟后降到约 1 次/分钟，
     * 内嵌 webview 亦同）。若按 `remainMs -= 1000` 累减，切走几分钟再回来仍显示
     * 「还有时间」，卡片既不到期也不刷新（禅道 Bug#23437）。
     */
    tick() {
      const next = this.deadlineTs
        ? this.deadlineTs - Date.now()
        : this.remainMs - 1000;
      this.remainMs = Math.max(0, next);
      if (this.remainMs === 0) this.handleZero();
    },
    // 回到前台：立刻校正一次，不必等被节流的计时器下一次回调
    onVisibilityChange() {
      if (document.visibilityState !== "visible") return;
      if (this.timer) this.tick();
    },
    clearTimer() {
      if (this.timer) clearInterval(this.timer);
      this.timer = null;
    },
    clearRecheck(resetCount = true) {
      if (this.recheckTimer) clearTimeout(this.recheckTimer);
      this.recheckTimer = null;
      if (resetCount) this.recheckCount = 0;
    },
    // 用最新的 order 数据重算剩余时长并重启计时（父级刷新/回填后调用）
    syncCountdown() {
      this.clearTimer();
      // 保留复查计数：后端迟迟不关单时，避免每次刷新都把上限清零导致无限轮询
      this.clearRecheck(false);
      this.remainMs = this.calcRemainMs();
      if (this.isUnpaid && this.remainMs <= 0) {
        // 刷新后仍是待支付且剩余为 0：说明后端尚未关单，不能就此停在 00′00″
        // （startTimer 不会启动，也就不会走 handleZero），直接安排复查
        this.scheduleRecheck();
        return;
      }
      // 拿到有效剩余 → 视为新一轮计时，复查计数归零
      this.recheckCount = 0;
      this.startTimer();
    },
    handleZero() {
      this.clearTimer();
      this.$emit("expired", this.order);
      // 后端到期关单有延迟（实测约 9~30s）：父级核对后若卡片仍是待支付，
      // 稍后自动再核对一次，避免卡片永久停在 00′00″ 且按钮不消失（Bug#23436）
      this.scheduleRecheck();
    },
    scheduleRecheck() {
      if (!this.isUnpaid || this.remainMs > 0) return;
      if (this.recheckCount >= ORDER_EXPIRE_RECHECK_MAX) return;
      this.recheckTimer = setTimeout(() => {
        this.recheckTimer = null;
        if (!this.isUnpaid || this.remainMs > 0) return;
        this.recheckCount += 1;
        this.$emit("expired", this.order);
        this.scheduleRecheck();
      }, ORDER_EXPIRE_RECHECK_INTERVAL);
    },
  },
  mounted() {
    this.startTimer();
    // 回到前台立即校正倒计时（后台期间计时器被节流，见 tick）
    document.addEventListener("visibilitychange", this.onVisibilityChange);
  },
  beforeDestroy() {
    this.clearTimer();
    if (this.recheckTimer) clearTimeout(this.recheckTimer);
    document.removeEventListener("visibilitychange", this.onVisibilityChange);
  },
};
</script>

<style lang="scss" scoped>
.order-item {
  position: relative;
  width: 1000px;
  background: #fff;
  border-radius: 15px;
  box-sizing: border-box;
}
.order-item-title {
  // 设计稿标题行高 59（18 + 22.5 + 18 + 1px 下边框）
  padding: 18px 16px;
  border-bottom: 1px solid #ebebeb;
  font-size: 20px;
  font-weight: 700;
  line-height: 22.5px;
  color: #3f454b;
  padding-right: 96px;
}
.order-item-body {
  // 设计稿：信息区上 20 / 左右 16 / 底部 10（按钮下沿到卡片底）
  padding: 20px 16px 10px;
}
.info-grid {
  display: flex;
  justify-content: space-between;
  // 设计稿：信息区到虚线 20px
  padding-bottom: 20px;
  // 仅当下方有操作行时才画分隔虚线（无操作行的卡片最后一行不带虚线）
  &.has-foot {
    border-bottom: 1px dashed #d7d7d7;
  }
}
.info-col {
  width: 313px;
  flex-shrink: 0;
}
.info-line {
  margin: 0 0 20px;
  font-size: 16px;
  line-height: 20px;
  color: #62717e;
  white-space: nowrap;
  &:last-child {
    margin-bottom: 0;
  }
  .info-label {
    color: #62717e;
  }
  .info-value {
    color: #333;
    &.is-bold {
      font-weight: 700;
    }
  }
}
.order-item-foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  // 设计稿：虚线到按钮 10px，按钮到卡片底 10px（由 .order-item-body 的 padding-bottom 提供）
  margin-top: 10px;
  min-height: 36px;
  box-sizing: border-box;
  .tip {
    font-size: 14px;
    color: #62717e;
    &.tip-orange {
      color: #ff831e;
    }
  }
  .actions {
    display: flex;
    align-items: center;
    gap: 15px;
  }
}
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 36px;
  padding: 0 20px;
  box-sizing: border-box;
  border-radius: 6px;
  font-size: 16px;
  cursor: pointer;
  &.btn-plain {
    color: #62717e;
    border: 1px solid #e9eef5;
    background: #fff;
  }
  &.btn-primary {
    width: 113px;
    padding: 0;
    color: #fff;
    background: #0fa87b;
  }
}
</style>
